import { useQuery } from '@tanstack/react-query';
import { API, graphqlOperation } from 'aws-amplify';
import { listStageCategorys } from '../../graphql/queries';

const ONE_HOUR = 1000 * 60 * 60;

function parseJson(value: unknown): Record<string, any> | null {
  if (!value) return null;
  if (typeof value === 'object') return value as Record<string, any>;
  try {
    return JSON.parse(value as string);
  } catch {
    return null;
  }
}

interface StageCategory {
  id: string;
  orgID: string;
  title: string;
  iconName?: string;
  iconType?: string;
  iconColor?: string;
  isDefault?: boolean;
  enablePindrop?: boolean;
  enableDeal?: boolean;
  defaultProductFamily?: string;
  flexAttributes?: Record<string, any> | null;
  createdBy?: string;
  lastUpdatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  isDeleted?: boolean;
}

async function fetchAllStageCategories(
  orgID: string
): Promise<StageCategory[]> {
  let nextToken: string | null = null;
  const all: StageCategory[] = [];

  do {
    const res: any = await API.graphql(
      graphqlOperation(listStageCategorys, {
        filter: { orgID: { eq: orgID }, isDeleted: { ne: true } },
        limit: 1000,
        nextToken,
      })
    );

    const items = (res?.data?.listStageCategorys?.items ?? [])
      .filter(Boolean)
      .map((item: any) => ({
        ...item,
        flexAttributes: parseJson(item.flexAttributes),
      }));
    all.push(...items);
    nextToken = res?.data?.listStageCategorys?.nextToken ?? null;
  } while (nextToken);

  return all;
}

export function useListStageCategories() {
  const orgID = (global as any)?.me?.orgID as string | undefined;

  return useQuery<StageCategory[], Error>({
    queryKey: ['stageCategories', orgID],
    queryFn: () => fetchAllStageCategories(orgID!),
    enabled: !!orgID,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
}
